import type { IBroadcastChannelManagerOptions } from './BroadcastChannelManager.types';

const DEBUG_STORAGE_KEY = 'debug:broadcast';

/**
 * Generic cross-tab state manager that synchronizes a value across
 * browser tabs using the BroadcastChannel API with localStorage
 * persistence. Lazily initializes browser APIs on first method call.
 *
 * Debug logging can be enabled at runtime by setting
 * localStorage.setItem('debug:broadcast', '1').
 *
 * @example
 * const manager = new BroadcastChannelManager({
 *   channelName: 'my-channel',
 *   storageKey: 'my-key',
 *   initialValue: 0,
 *   serialize: (v) => String(v),
 *   deserialize: (raw) => parseInt(raw, 10),
 * });
 * manager.setValue(42);
 */
export class BroadcastChannelManager<T> {
  private channel: BroadcastChannel | null = null;
  private storageKey: string;
  private channelName: string;
  private currentValue: T;
  private serialize: (value: T) => string;
  private deserialize: (raw: string) => T;
  private listeners: Set<(value: T) => void> = new Set();
  private initialized = false;

  constructor(options: IBroadcastChannelManagerOptions<T>) {
    this.channelName = options.channelName;
    this.storageKey = options.storageKey;
    this.currentValue = options.initialValue;
    this.serialize = options.serialize;
    this.deserialize = options.deserialize;
  }

  private get isBrowser(): boolean {
    return typeof window !== 'undefined';
  }

  protected debug(...args: unknown[]): void {
    if (!this.isBrowser) {
      return;
    }
    const enabled = localStorage.getItem(DEBUG_STORAGE_KEY);
    if (!enabled) {
      return;
    }
    console.log(`[BroadcastManager:${this.channelName}]`, ...args);
  }

  private ensureInitialized(): void {
    if (this.initialized || !this.isBrowser) {
      return;
    }

    this.initialized = true;

    const stored = localStorage.getItem(this.storageKey);
    if (stored !== null) {
      this.currentValue = this.deserialize(stored);
      this.debug('restored from storage:', this.currentValue);
    }

    this.channel = new BroadcastChannel(this.channelName);
    this.channel.onmessage = (event: MessageEvent): void => {
      this.currentValue = event.data.value as T;
      localStorage.setItem(this.storageKey, this.serialize(this.currentValue));
      this.debug('received from tab:', this.currentValue);
      this.listeners.forEach((fn) => fn(this.currentValue));
    };

    this.debug('initialized, value:', this.currentValue);
  }

  getValue(): T {
    this.ensureInitialized();
    this.debug('getValue:', this.currentValue);
    return this.currentValue;
  }

  setValue(value: T): void {
    this.ensureInitialized();
    this.currentValue = value;
    this.persist();
    this.broadcast();
    this.debug('setValue:', value);
  }

  subscribe(listener: (value: T) => void): () => void {
    this.ensureInitialized();
    this.listeners.add(listener);
    this.debug('subscribe, listeners:', this.listeners.size);

    return (): void => {
      this.listeners.delete(listener);
      this.debug('unsubscribe, listeners:', this.listeners.size);
    };
  }

  destroy(): void {
    this.debug('destroy');
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    this.listeners.clear();
    this.initialized = false;
  }

  private persist(): void {
    if (!this.isBrowser) {
      return;
    }
    localStorage.setItem(this.storageKey, this.serialize(this.currentValue));
  }

  private broadcast(): void {
    if (!this.channel) {
      return;
    }
    this.channel.postMessage({ value: this.currentValue });
  }
}
